import React from "react";
import { Input } from "antd";

// ************************************************ */
// SHARED U128 TYPE AND FIELD RENDERER ************* */
//
// A 128-bit value carried as two decimal strings (high/low 64 bits), used
// wherever a Numato board command needs a mask or bitmask wider than a
// uint8 -- the GPIO board's Write All / IO Direction / IO Mask / Power On
// commands, and the relay board's wide Write All command. Moved here so
// both editors share one definition and one renderer instead of drifting.
export interface U128 {
  high: string;
  low: string;
}

export function defaultU128(): U128 {
  return { high: "0", low: "0" };
}

/**
 * The `renderU128Fields` `label` argument every caller of a single-valued
 * U128 command (relay `RelayWriteAllMessage`; GPIO `WriteAllMessage`,
 * `IoDirection`, `IoMask`) passes. N3: `commandU128Problems` (below) names
 * its problem sentences from this same constant, so a problem always calls
 * the field what the UI itself calls it — "Value High"/"Value Low" — rather
 * than a hand-copied string that can drift from the renderer's.
 */
export const U128_VALUE_LABEL = "Value";

/** The two `renderU128Fields` labels GPIO `PowerOn` passes — N3, as above. */
export const U128_VALUE_0_LABEL = "Value 0";
export const U128_VALUE_1_LABEL = "Value 1";

/** The largest value a 64-bit half can carry: 2**64 - 1. */
const U128_HALF_MAX = BigInt("18446744073709551615");

const U128_HALF_ERROR_MESSAGE = "Whole number from 0 to 18446744073709551615";

/**
 * True exactly when `s` is a plain decimal digit string (no sign, no
 * whitespace, no decimal point, no hex notation) whose value fits in a
 * uint64 -- the digit shape LiftCloud's save-time check
 * (`parseNumatoU128HalfStrict`) parses. Any other string is rejected by both:
 * LiftCloud answers the save with a 400. The client is deliberately stricter
 * in one case: LiftCloud treats an empty, `null` or absent half as
 * "unassigned" and stores 0 in that half's 64 bits, while the client rejects
 * all three, so a half the operator left blank is caught in the form rather
 * than saved as zeros.
 *
 * N2: this is the ONE rule both the renderer (`renderU128Fields`, below) and
 * the walker (`commandU128Problems`, further down) apply — deliberately
 * taking `unknown` rather than `string`, so a half that is not actually a
 * string at runtime (a hydrated/legacy CDO can carry a number, e.g. `high:
 * 0`) is rejected here too. Checking `typeof s === "string"` inline at every
 * call site would work just as well until one of them forgot to; putting the
 * check inside this one function makes forgetting impossible. A bare
 * `/^[0-9]+$/.test(s)` on a non-string `s` would silently coerce it via
 * `String(s)` and could accept what should be rejected — `test(0)` matches
 * the same as `test("0")`.
 */
export function u128HalfIsValid(s: unknown): boolean {
  if (typeof s !== "string") return false;
  if (!/^[0-9]+$/.test(s)) return false;
  return BigInt(s) <= U128_HALF_MAX;
}

/**
 * Render the High/Low input pair for a U128 value.
 *
 * `testIdPrefix` is optional and only sets `data-testid` when the caller
 * provides one — the GPIO editor passes nothing, so its rendered output
 * (and the wizard's scanned docs surface for it) is unchanged. The relay
 * editor's wide Write All is the one caller that passes a prefix.
 *
 * An invalid half (see `u128HalfIsValid`) gets its `Input` an antd error
 * status and a short message underneath, so the operator sees the problem
 * before it silently saves as zero.
 */
export function renderU128Fields(
  label: string,
  value: U128,
  onChangeU128: (updated: U128) => void,
  testIdPrefix?: string,
) {
  const highValid = u128HalfIsValid(value.high);
  const lowValid = u128HalfIsValid(value.low);
  return (
    <div className="row">
      <div className="col-6 mt-2">
        <span className="generalStyles__inputLabel">{label} High</span>
        <Input
          size="small"
          status={highValid ? undefined : "error"}
          value={value.high}
          onChange={(e) => onChangeU128({ ...value, high: e.target.value })}
          style={{ marginTop: 4 }}
          data-testid={testIdPrefix ? `${testIdPrefix}-high` : undefined}
        />
        {!highValid && (
          <span style={{ fontSize: 10, color: "#ff4d4f", display: "block" }}>
            {U128_HALF_ERROR_MESSAGE}
          </span>
        )}
      </div>
      <div className="col-6 mt-2">
        <span className="generalStyles__inputLabel">{label} Low</span>
        <Input
          size="small"
          status={lowValid ? undefined : "error"}
          value={value.low}
          onChange={(e) => onChangeU128({ ...value, low: e.target.value })}
          style={{ marginTop: 4 }}
          data-testid={testIdPrefix ? `${testIdPrefix}-low` : undefined}
        />
        {!lowValid && (
          <span style={{ fontSize: 10, color: "#ff4d4f", display: "block" }}>
            {U128_HALF_ERROR_MESSAGE}
          </span>
        )}
      </div>
    </div>
  );
}

// ************************************************ */
// SERVER-REJECTION MIRROR — U128 HALVES *********** */

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
}

/**
 * Push a problem sentence when `half` is not a valid U128 half. Never
 * throws. N2: this is the walker's only call into the shared rule —
 * `u128HalfIsValid` itself owns the "must be a string" check, so this does
 * not repeat it.
 */
function checkHalf(problems: string[], half: unknown, halfLabel: string, prefix: string): void {
  if (!u128HalfIsValid(half)) {
    problems.push(`${prefix}: ${halfLabel} must be a whole number from 0 to 18446744073709551615.`);
  }
}

/** A CDO's U128 walk is scoped to exactly one protocol's command shapes —
 *  never guessed from content.kind alone, because a relay board's narrow
 *  Write All (`{ kind: "RelayCommandContentTable", content: { kind:
 *  "WriteAllMessage", message: 7 } }`) and a GPIO board's wide Write All
 *  (`{ kind: "GpioCommandContentTable", content: { kind: "WriteAllMessage",
 *  value: U128 } }`) share the same inner `content.kind` string but carry
 *  different fields. */
export type NumatoU128Protocol = "relay" | "gpio";

/**
 * Every U128 problem carried by a single command's content, scoped to
 * `protocol`.
 *
 * Relay: only `{ kind: "RelayCommandContentTable", content: { kind:
 * "RelayWriteAllMessage" } }` carries a U128 (`content.value`) — the narrow
 * Write All's `content.value` (uint8) has no U128 fields at all.
 *
 * GPIO: only `{ kind: "GpioCommandContentTable", content: {...} }` carries
 * one — `WriteAllMessage`, `IoDirection` and `IoMask` in `content.value`,
 * and `PowerOn` in `content.value_0`/`content.value_1` (see
 * `CdoNumatoGpioEditor.tsx`'s `renderGpioContentFields`).
 *
 * A command whose outer `kind` does not match the protocol's own wrapper is
 * not a shape this protocol's board can even carry, so it is left alone —
 * never inspected, never flagged.
 */
function commandU128Problems(
  cmd: unknown,
  table: string,
  row: number,
  protocol: NumatoU128Protocol,
): string[] {
  const problems: string[] = [];
  const cmdRecord = asRecord(cmd);
  const outerKind = protocol === "relay" ? "RelayCommandContentTable" : "GpioCommandContentTable";
  if (cmdRecord?.kind !== outerKind) return problems;
  const content = asRecord(cmdRecord.content);
  if (!content) return problems;
  const prefix = `${table} mapping #${row}`;
  const kind = content.kind;

  if (protocol === "relay") {
    if (kind !== "RelayWriteAllMessage") return problems;
    // A missing `value` object entirely — or explicitly `null` (N-a: LiftCloud
    // decodes a null half as zero, and the renderer already treats it as the
    // default via `??`) — is not a problem: LiftCloud defaults it to zero, and
    // the editor shows 0/0 — see numatoU128Problems' doc.
    if (content.value == null) return problems;
    const value = asRecord(content.value);
    checkHalf(problems, value?.high, `${U128_VALUE_LABEL} High`, prefix);
    checkHalf(problems, value?.low, `${U128_VALUE_LABEL} Low`, prefix);
    return problems;
  }

  // GPIO
  if (kind === "WriteAllMessage" || kind === "IoDirection" || kind === "IoMask") {
    if (content.value == null) return problems;
    const value = asRecord(content.value);
    checkHalf(problems, value?.high, `${U128_VALUE_LABEL} High`, prefix);
    checkHalf(problems, value?.low, `${U128_VALUE_LABEL} Low`, prefix);
  } else if (kind === "PowerOn") {
    if (content.value_0 != null) {
      const value0 = asRecord(content.value_0);
      checkHalf(problems, value0?.high, `${U128_VALUE_0_LABEL} High`, prefix);
      checkHalf(problems, value0?.low, `${U128_VALUE_0_LABEL} Low`, prefix);
    }
    if (content.value_1 != null) {
      const value1 = asRecord(content.value_1);
      checkHalf(problems, value1?.high, `${U128_VALUE_1_LABEL} High`, prefix);
      checkHalf(problems, value1?.low, `${U128_VALUE_1_LABEL} Low`, prefix);
    }
  }
  return problems;
}

/**
 * Every reason the server would reject a U128 half carried anywhere in a
 * parsed relay or GPIO CDO's `response_to_message`/`message_to_cmd` tables,
 * scoped to `protocol` — the caller must say which board's contract this CDO
 * follows, rather than the walk guessing it from shape (see Fix 1: a narrow
 * relay Write All and a wide GPIO Write All share the inner `content.kind`
 * string "WriteAllMessage" but not the same fields). Never throws on
 * malformed input -- defensive at every step, the same way
 * `numatoRelayControllerProblems` is. [] means every U128 half found is
 * saveable as-is (including when there are none at all).
 */
export function numatoU128Problems(parsedCdo: unknown, protocol: NumatoU128Protocol): string[] {
  const cdo = asRecord(parsedCdo);
  if (!cdo) return [];
  const problems: string[] = [];

  const responseToMessage = Array.isArray(cdo.response_to_message) ? cdo.response_to_message : [];
  responseToMessage.forEach((entry, idx) => {
    const key = asRecord(asRecord(entry)?.key);
    problems.push(...commandU128Problems(key?.command, "Response to Message", idx + 1, protocol));
  });

  const messageToCmd = Array.isArray(cdo.message_to_cmd) ? cdo.message_to_cmd : [];
  messageToCmd.forEach((entry, idx) => {
    const value = asRecord(entry)?.value;
    problems.push(...commandU128Problems(value, "Message to Command", idx + 1, protocol));
  });

  return problems;
}
